import React, { useEffect, useState } from 'react'
import { DropZone, Radio, IdScanRadioGroup } from '../../../ui'
import { compose, find, head, map, prop, propEq, propOr, sort, isEmpty } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../StateProvider'
import { usePrevious } from '../../utils/hooks'
import { getUserValue } from '../utils'

const Item = ({ idType }) => {
  const { t } = useTranslation()
  return <Radio value={idType} title={t(`verifyForm.${idType}`)} />
}
/**
 *
 * @param {string} idType
 * @param {IdType[]} idTypes
 * @returns {IdType}
 */
const getIdType = (idType, idTypes) =>
  find(propEq('idType', idType))(idTypes)

/**
 *
 * @param {Country} country
 * @returns {IdType[]}
 */
const getIdTypes = (country) => (country ? country.idTypes : [])
/**
 *
 * @param {string} countryCode
 * @returns {function}
 */
const getCountry = (countryCode) => find(propEq('countryCode', countryCode))
/**
 *
 * @param {string} countryCode
 * @param {Country[]} countries
 * @returns {IdType[]}
 */
const getIdTypesByCountry = (countryCode, countries) =>
  compose(
    sort((idTypeObj1, idTypeObj2) => idTypeObj1.idType.localeCompare(idTypeObj2.idType)),
    getIdTypes,
    getCountry(countryCode)
  )(countries)

const ExternalIdScanFields = ({ showLabelAsPlaceholder = false }) => {
  const [statesManager, setStatesManager] = useState({
    idType: '',
    idTypes: [],
    backSide: false,
    idScanError: '',
    idBackSideScanError: '',
    idScan: {},
    idBackSideScan: {},
    validation: {},
    errors: {}
  })
  const [isValuePreset, setIsValuePreset] = useState(false)
  const [isBacksideValuePreset, setIsBacksideValuePreset] = useState(false)

  const {
    idType,
    idTypes,
    backSide,
    idScanError,
    idBackSideScanError,
    validation,
    idScan,
    idBackSideScan
  } = statesManager

  const [{ form: { accId }, user, form, data: { citizenshipCountries: countries } }, dispatch] = useStore()
  const idScanStore = getUserValue(accId, user, 'idScan')
  const idBackSideScanStore = getUserValue(accId, user, 'idBackSideScan')
  const { citizenshipCountry } = form[accId]
  const idTypeUser = getUserValue(accId, user, 'idType')

  const prevCountries = usePrevious(countries)
  const prevCitizenship = usePrevious(citizenshipCountry)
  const { t } = useTranslation()

  useEffect(() => {
    if (!backSide) {
      dispatch({ type: 'removeFieldValue', name: 'idBackSideScan' })
      dispatch({ type: 'removeValidationFld', name: 'idBackSideScan' })
      dispatch({ type: 'setFlag', name: 'isIdBackSideScan', value: false })
      setStatesManager(prevState => ({
        ...prevState,
        idBackSideScan: {},
        validation: {
          ...prevState.validation,
          idBackSideScan: false
        }
      }))
    }
  }, [backSide])

  useEffect(() => {
    const isCountriesReceivedFirstTime = (!prevCountries || !prevCountries.length) &&
      (countries && countries.length)
    const ifCitizenshipChanged = prevCitizenship !== citizenshipCountry

    if (isCountriesReceivedFirstTime || ifCitizenshipChanged) {
      let addState
      let addValidation
      if (citizenshipCountry && countries.length) {
        ({ addState, addValidation } = getDocumentsFields())
      } else {
        ({ addState, addValidation } = resetDocumentsFields())
        dispatch({ type: 'removeFieldValue', name: 'idScan' })
        dispatch({ type: 'removeFieldValue', name: 'idBackSideScan' })
        dispatch({ type: 'setValidationFld', name: 'idScan', value: false })
        dispatch({ type: 'setValidationFld', name: 'idBackSideScan', value: false })
        setStatesManager(prevState => ({
          ...prevState,
          idScan: {},
          validation: {
            ...prevState.validation,
            idScan: false,
            idBackSideScan: false
          }
        }))
      }
      setStatesManager(prevState => ({
        ...prevState,
        ...addState,
        validation: {
          ...prevState.validation, ...addValidation
        }
      }))
      dispatch({
        type: 'setExtFieldValue', name: 'idType', value: prop('idType', addState)
      })
      dispatch({
        type: 'setExtFieldValue', name: 'backSide', value: prop('backSide', addState)
      })
      if (addState.backSide) {
        if (!validation.idBackSideScan) {
          dispatch({ type: 'addValidationFld', name: 'idBackSideScan' })
          dispatch({ type: 'setFlag', name: 'isIdBackSideScan', value: true })
        }
      } else {
        dispatch({ type: 'removeValidationFld', name: 'idBackSideScan' })
        dispatch({ type: 'setFlag', name: 'isIdBackSideScan', value: false })
      }
    }
  }, [countries, citizenshipCountry, idTypeUser])

  useEffect(() => {
    if (isValuePreset) return
    if (idScanStore) {
      setStatesManager(prevState => ({ ...prevState, idScan: idScanStore, validation: { ...prevState.validation, idScan: true } }))
      dispatch({ type: 'setExtFieldValue', name: 'idScan', value: idScanStore })
      dispatch({ type: 'setValidationFld', name: 'idScan', value: true })
    }
    setIsValuePreset(true)
  }, [isValuePreset, idScanStore])

  useEffect(() => {
    if (isBacksideValuePreset) return
    if (idBackSideScanStore) {
      setStatesManager(prevState => ({ ...prevState, idBackSideScan: idBackSideScanStore, validation: { ...prevState.validation, idBackSideScan: true } }))
      dispatch({ type: 'setExtFieldValue', name: 'idBackSideScan', value: idBackSideScanStore })
      dispatch({ type: 'setValidationFld', name: 'idBackSideScan', value: true })
    }
    setIsBacksideValuePreset(true)
  }, [isBacksideValuePreset, idBackSideScanStore])

  const getDocumentsFields = () => {
    let addState
    let addValidation
    try {
      const idTypes = getIdTypesByCountry(citizenshipCountry, countries)
      const idType = idTypeUser || head(idTypes).idType
      let { acquisitionConfig: { backSide } } = head(idTypes)
      if (idTypeUser) {
        const idTypeObj = idTypes.find(({ idType }) => idType === idTypeUser)
        backSide = idTypeObj?.acquisitionConfig?.backSide
      }

      addState = {
        idTypes,
        idType,
        backSide
      }
      addValidation = {
        idType: true
      }
    } catch (e) {
      ({ addState, addValidation } = resetDocumentsFields())
      console.warn('no idTypes data for ', citizenshipCountry)
    }
    return { addState, addValidation }
  }

  const resetDocumentsFields = () => {
    const addState = {
      idTypes: [],
      idType: '',
      backSide: false,
      idScan: {},
      idBackSideScan: {}
    }
    const addValidation = {
      idType: false
    }
    return { addState, addValidation }
  }

  /**
   * @param {string} itemName
   * @param {string} message
   */
  const onDropReject = (itemName, message) => {
    setStatesManager(prevState => ({
      ...prevState,
      [itemName]: {},
      [`${itemName}Error`]: message,
      validation: {
        ...prevState.validation,
        [itemName]: false
      }
    }))
    dispatch({
      type: 'setExtFieldValue', name: itemName, value: {}
    })
    dispatch({ type: 'setValidationFld', name: itemName, value: false })
  }

  /**
   *
   * @param {string} itemName
   * @param {FileImg[]} file
   */
  const onDropAccept = (itemName, file) => {
    setStatesManager(prevState => ({
      ...prevState,
      [itemName]: file,
      [`${itemName}Error`]: '',
      validation: {
        ...prevState.validation,
        [itemName]: true
      }
    }))
    dispatch({
      type: 'setExtFieldValue', name: itemName, value: file
    })
    dispatch({ type: 'setValidationFld', name: itemName, value: true })
  }

  /**
   *
   * @param {string} itemName
   */
  const onRemoveUploadedHandler = (itemName) => {
    setStatesManager(prevState => ({
      ...prevState,
      [itemName]: {},
      [`${itemName}Error`]: '',
      validation: {
        ...prevState.validation,
        [itemName]: false
      }
    }))

    dispatch({
      type: 'setExtFieldValue', name: itemName, value: {}
    })
    dispatch({ type: 'setValidationFld', name: itemName, value: false })
  }

  const onChooseTypeChange = ({ currentTarget: { name, value } }) => {
    const {
      acquisitionConfig: { backSide }
    } = getIdType(value, idTypes)
    setStatesManager(prevState => ({
      ...prevState,
      backSide,
      [name]: value,
      validation: {
        ...prevState.validation,
        [name]: true
      }
    }))

    dispatch({
      type: 'setExtFieldValue', name: 'idType', value
    })
    dispatch({
      type: 'setExtFieldValue', name: 'backSide', value: backSide
    })
    if (backSide) {
      dispatch({ type: 'addValidationFld', name: 'idBackSideScan' })
      dispatch({ type: 'setFlag', name: 'isIdBackSideScan', value: true })
      if (!isEmpty(idBackSideScan)) {
        dispatch({ type: 'setValidationFld', name: 'idBackSideScan', value: true })
      }
    } else {
      dispatch({ type: 'removeValidationFld', name: 'idBackSideScan' })
      dispatch({ type: 'setFlag', name: 'isIdBackSideScan', value: false })
    }
  }

  return (
    !!idTypes.length && (
      <>
        <IdScanRadioGroup
          label={t('verifyForm.chooseType')}
          name='idType'
          selectedValue={idType}
          onChange={onChooseTypeChange}
          valid={propOr(false, 'idType', validation)}
          showLabel={!showLabelAsPlaceholder}
        >
          {map(
            idTypeObj => (
              <Item idType={idTypeObj.idType} key={idTypeObj.idType} />
            ),
            idTypes
          )}
        </IdScanRadioGroup>
        <DropZone
          label='verifyForm.uploadFrontside'
          photo={idScan}
          name='idScan'
          error={t(idScanError)}
          valid={propOr(false, 'idScan', validation)}
          onDropReject={onDropReject}
          onDropAccept={onDropAccept}
          onRemoveUploaded={onRemoveUploadedHandler}
        />
        {backSide && (
          <DropZone
            label='verifyForm.uploadBackside'
            photo={idBackSideScan}
            name='idBackSideScan'
            error={t(idBackSideScanError)}
            valid={propOr(false, 'idBackSideScan', validation)}
            onDropReject={onDropReject}
            onDropAccept={onDropAccept}
            onRemoveUploaded={onRemoveUploadedHandler}
          />
        )}
      </>
    ))
}

const getExternalIdScanField = field => {
  const { name, order } = field
  return (
    <ExternalIdScanFields
      name={name}
      key={name}
      order={order}
    />
  )
}

export { ExternalIdScanFields, getExternalIdScanField }
